import { useMutation, useQuery, useQueryClient, } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ApiStatus } from '../api';
import { offlineAction } from '../functions';
const holidayQueryName = ['holiday'];
/* eslint-disable  @typescript-eslint/no-explicit-any */
const simpleWrapperOfflineMutation = async (mutationFunc, func, props) => {
    try {
        return await mutationFunc.mutateAsync(props ? { ...props } : undefined, {
            onSuccess: () => {
                if (func) {
                    func();
                }
            },
        });
    }
    catch (e) {
        offlineAction(e);
    }
};
export const useHolidayBooking = (api, bookingId) => {
    const holidayBookingQueryKey = useMemo(() => [...holidayQueryName, bookingId], [bookingId]);
    const queryClient = useQueryClient();
    const sendDecisionMutation = useMutation({
        networkMode: 'always',
        mutationFn: async (decision) => api.holiday.sendDecision(decision),
        onMutate: () => {
            queryClient.cancelQueries({ queryKey: holidayQueryName });
            queryClient.setQueryData(holidayBookingQueryKey, {
                status: ApiStatus.Loading,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(holidayQueryName);
        },
    });
    const sendDecision = () => {
        const mutationAsync = async (setData, setError, props) => {
            try {
                await sendDecisionMutation.mutateAsync(props ? { ...props } : {}, {
                    onSuccess: (res) => {
                        if (setData) {
                            setData({
                                success: true,
                                message: res.data.message,
                            });
                        }
                    },
                    onError: (e) => {
                        if (setError) {
                            setError(e);
                        }
                    },
                });
            }
            catch (e) {
                offlineAction(e);
            }
        };
        const mutation = (setData, setError, props) => {
            try {
                sendDecisionMutation.mutate(props ? { ...props } : {}, {
                    onSuccess: (res) => {
                        if (setData) {
                            setData({
                                success: true,
                                message: res.data.message,
                            });
                        }
                    },
                    onError: (e) => {
                        if (setError) {
                            setError(e);
                        }
                    },
                });
            }
            catch (e) {
                offlineAction(e);
            }
        };
        return {
            mutationAsync,
            mutation,
            isLoading: sendDecisionMutation.isLoading,
        };
    };
    const sendCancellationDecisionMutation = useMutation({
        networkMode: 'always',
        mutationFn: async (decision) => api.holiday.sendCancellationDecision(decision),
        onMutate: () => {
            queryClient.cancelQueries({ queryKey: holidayQueryName });
            queryClient.setQueryData(holidayBookingQueryKey, {
                status: ApiStatus.Loading,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(holidayQueryName);
        },
    });
    const sendCancellationDecision = () => {
        const mutationAsync = async (props, setData, setError, rejectionReason) => {
            try {
                await sendCancellationDecisionMutation.mutateAsync({ ...props, rejectionReason: rejectionReason }, {
                    onSuccess: (res) => {
                        setData(res.data);
                        setError(null);
                    },
                });
            }
            catch (e) {
                offlineAction(e);
            }
        };
        const mutation = (props, setData, setError, rejectionReason) => {
            try {
                sendCancellationDecisionMutation.mutate({ ...props, rejectionReason: rejectionReason }, {
                    onSuccess: (res) => {
                        setData(res.data);
                        setError(null);
                    },
                });
            }
            catch (e) {
                offlineAction(e);
            }
        };
        return {
            mutationAsync,
            mutation,
            isLoading: sendCancellationDecisionMutation.isLoading,
        };
    };
    const withdrawBookingMutation = useMutation({
        networkMode: 'always',
        mutationFn: async () => api.holiday.withdrawHolidayBooking(bookingId),
        onMutate: () => {
            queryClient.cancelQueries({ queryKey: holidayQueryName });
            queryClient.setQueryData(holidayBookingQueryKey, {
                status: ApiStatus.Loading,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(holidayQueryName);
        },
    });
    const withdrawBooking = async (func) => {
        return await simpleWrapperOfflineMutation(withdrawBookingMutation, func);
    };
    const sendRequestReminderMutation = useMutation({
        networkMode: 'always',
        mutationFn: async () => api.holiday.sendApprovalRequestReminder(bookingId),
        onMutate: () => {
            queryClient.cancelQueries({ queryKey: holidayQueryName });
            queryClient.setQueryData(holidayBookingQueryKey, {
                status: ApiStatus.Loading,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(holidayQueryName);
        },
    });
    const sendRequestReminder = async (func) => {
        return await simpleWrapperOfflineMutation(sendRequestReminderMutation, func);
    };
    const sendCancellationRequestReminderMutation = useMutation({
        networkMode: 'always',
        mutationFn: async () => api.holiday.sendCancellationApprovalRequestReminder(bookingId),
        onMutate: () => {
            queryClient.cancelQueries({ queryKey: holidayQueryName });
            queryClient.setQueryData(holidayBookingQueryKey, {
                status: ApiStatus.Loading,
            });
        },
        onSuccess: () => {
            if (!navigator.onLine) {
                return;
            }
            queryClient.invalidateQueries(holidayQueryName);
        },
    });
    const sendCancellationRequestReminder = async (func) => {
        return await simpleWrapperOfflineMutation(sendCancellationRequestReminderMutation, func);
    };
    const cancelBookingMutation = useMutation({
        networkMode: 'always',
        mutationFn: async () => api.holiday.cancelBooking(bookingId),
        onMutate: () => {
            queryClient.cancelQueries({ queryKey: holidayQueryName });
            queryClient.setQueryData(holidayBookingQueryKey, {
                status: ApiStatus.Loading,
            });
        },
        onSuccess: () => {
            if (!navigator.onLine) {
                return;
            }
            queryClient.invalidateQueries(holidayQueryName);
        },
    });
    const cancelBooking = async (func) => {
        return await simpleWrapperOfflineMutation(cancelBookingMutation, func);
    };
    const holidayStatus = useQuery({
        queryKey: holidayBookingQueryKey,
        queryFn: async () => {
            if (bookingId === '') {
                return null;
            }
            const { data } = await api.holiday.getHolidayStatus(bookingId);
            return data;
        },
        staleTime: Infinity,
    });
    return {
        sendDecision,
        sendCancellationDecision,
        holidayStatus,
        withdrawBooking,
        sendRequestReminder,
        sendCancellationRequestReminder,
        cancelBooking,
    };
};
export const useHoliday = (api) => {
    const holidayBookings = useQuery({
        queryKey: [...holidayQueryName, 'bookings'],
        queryFn: async () => {
            const apiResponse = await api.holiday.getHolidayBookings();
            if (apiResponse.status === ApiStatus.Failed) {
                throw Error(apiResponse.errorText);
            }
            return apiResponse.data;
        },
        staleTime: Infinity,
    });
    const holidaysFilteredMutation = useMutation({
        networkMode: 'always',
        mutationFn: async (filter) => api.holiday.getHolidayBookFormFiltered(filter),
    });
    const holidaysFiltered = () => {
        const mutation = (newApprovalStatuses, newRadioStatus, setHolidays) => {
            try {
                holidaysFilteredMutation.mutate({
                    approvalStatuses: newApprovalStatuses,
                    bookingTimeStatus: newRadioStatus,
                }, {
                    onSuccess: (res) => {
                        if (res.data) {
                            const holidayData = res.data.sort((h1, h2) => +new Date(h2.startDate) - +new Date(h1.startDate));
                            if (setHolidays) {
                                setHolidays(holidayData);
                            }
                        }
                    },
                });
            }
            catch (e) {
                offlineAction(e);
            }
        };
        return {
            mutation,
            isLoading: holidaysFilteredMutation.isLoading,
        };
    };
    const submitHolidayMutation = useMutation({
        networkMode: 'always',
        mutationFn: async (bookingForm) => api.holiday.addHolidayBooking(bookingForm),
    });
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    const submitHoliday = async (func, props) => {
        try {
            await submitHolidayMutation.mutateAsync(props, {
                onSuccess: () => {
                    func();
                },
            });
        }
        catch (e) {
            offlineAction(e);
        }
    };
    return {
        holidayBookings,
        holidaysFiltered,
        submitHoliday,
    };
};
export const useHolidayRequest = (api, approvalStatuses, directReportIds) => {
    const holidayRequests = useQuery({
        queryKey: [...holidayQueryName, 'requests', { approvalStatuses, directReportIds }],
        queryFn: async () => {
            const apiResponse = await api.holiday.getHolidayRequests(approvalStatuses);
            if (apiResponse.status === ApiStatus.Failed || !apiResponse.data) {
                throw Error(apiResponse.errorText);
            }
            return apiResponse.data;
        },
        staleTime: Infinity,
    });
    return {
        holidayRequests,
    };
};
